/*
 * Labelled native select drawn like the Tasks tab's "Call eligibility" filter: own chevron
 * inset 10px with 30px right padding, 34px tall (40px on phones), full width on phones.
 */
import React from 'react';
import { FaChevronDown } from 'react-icons/fa';

export interface LeadsSelectOption {
  value: string;
  label: string;
}

export interface LeadsSelectGroup {
  label: string;
  options: LeadsSelectOption[];
}

interface Props {
  id: string;
  label: string;
  value: string;
  options: LeadsSelectOption[];
  // When set, rendered as <optgroup>s instead of `options`.
  groups?: LeadsSelectGroup[];
  onChange: (value: string) => void;
  // Leading option with value "" (e.g. "Choose a team member").
  placeholder?: string;
  disabled?: boolean;
  width?: number;
  // Brand border, as the Tasks filters use for a set (non-default) value.
  active?: boolean;
  testId?: string;
}

function LeadsSelect({
  id,
  label,
  value,
  options,
  groups,
  onChange,
  placeholder,
  disabled,
  width = 150,
  active,
  testId
}: Props) {
  return (
    <div className="inline-flex min-w-0 items-center gap-2 max-sm:w-full">
      <label
        htmlFor={id}
        className="whitespace-nowrap text-xs text-content-muted"
        style={{ margin: 0, fontWeight: 400, textTransform: 'none' }}
      >
        {label}
      </label>
      <span className="relative inline-flex min-w-0 items-center max-sm:flex-1">
        <select
          id={id}
          value={value}
          disabled={disabled}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
          // index.css forces select { height: 46px !important }; match the 34px Tasks triggers.
          className="appearance-none !h-[34px] max-w-full max-sm:!h-10 max-sm:!w-full"
          style={{
            width,
            boxSizing: 'border-box',
            margin: 0,
            padding: '6px 30px 6px 10px',
            fontSize: 13,
            lineHeight: '20px',
            borderRadius: 8,
            border: '1px solid',
            borderColor: active ? 'var(--brand-primary)' : 'var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--text-primary)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
          {groups
            ? groups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              ))
            : options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
        </select>
        <FaChevronDown
          aria-hidden="true"
          size={11}
          className="pointer-events-none absolute"
          style={{ right: 10, color: 'var(--text-muted)' }}
        />
      </span>
    </div>
  );
}

export default LeadsSelect;
